import type { TableRow } from "../issuesTableConfig";

export type CountRow = {
  name: string;
  values: Record<string, number>;
};

export type TrialIssuesAnalyticsData = {
  totalIssues: number;
  distinctSubjects: number;
  distinctSites: number;
  distinctRules: number;
  avgDaysOpen: string;
  byStudyStatus: CountRow[];
  bySource: CountRow[];
  byAssignee: CountRow[];
  byCategory: CountRow[];
  ageBuckets: CountRow[];
  rulesBySource: CountRow[];
};

const getStringValue = (value: TableRow[string]) =>
  typeof value === "string" && value.trim().length > 0 ? value : null;

const countBy = (rows: TableRow[], key: string) => {
  const counts = new Map<string, number>();

  rows.forEach((row) => {
    const value = getStringValue(row[key]) || "Unspecified";
    counts.set(value, (counts.get(value) || 0) + 1);
  });

  return counts;
};

const toCountRows = (
  counts: Map<string, number>,
  valueLabel = "Issues",
  limit?: number,
  options?: { sort?: boolean },
): CountRow[] => {
  const entries = Array.from(counts.entries());
  const orderedEntries =
    options?.sort === false
      ? entries
      : [...entries].sort((left, right) => right[1] - left[1]);
  const rows = orderedEntries.map(([name, value]) => ({
    name,
    values: { [valueLabel]: value },
  }));

  return typeof limit === "number" ? rows.slice(0, limit) : rows;
};

const getAverageDaysOpen = (rows: TableRow[]) => {
  const values = rows
    .map((row) => Number(row.discrepancy_days_open))
    .filter((value) => !Number.isNaN(value));

  if (values.length === 0) return 0;

  return values.reduce((sum, value) => sum + value, 0) / values.length;
};

const createAgeBuckets = (rows: TableRow[]) => {
  const buckets = new Map<string, number>([
    ["0-1 days", 0],
    ["2-3 days", 0],
    ["4-5 days", 0],
    ["6+ days", 0],
  ]);

  rows.forEach((row) => {
    const value = Number(row.discrepancy_days_open);

    if (Number.isNaN(value)) return;
    if (value <= 1) {
      buckets.set("0-1 days", (buckets.get("0-1 days") || 0) + 1);
      return;
    }
    if (value <= 3) {
      buckets.set("2-3 days", (buckets.get("2-3 days") || 0) + 1);
      return;
    }
    if (value <= 5) {
      buckets.set("4-5 days", (buckets.get("4-5 days") || 0) + 1);
      return;
    }

    buckets.set("6+ days", (buckets.get("6+ days") || 0) + 1);
  });

  return buckets;
};

export const buildTrialIssuesAnalytics = (
  issuesData: TableRow[],
  rulesData: TableRow[],
): TrialIssuesAnalyticsData => {
  const distinctSubjects = new Set(
    issuesData.map((row) => getStringValue(row.subject)).filter(Boolean),
  ).size;
  const distinctSites = new Set(
    issuesData.map((row) => getStringValue(row.site)).filter(Boolean),
  ).size;
  const distinctRules = new Set(
    rulesData
      .map((row) => getStringValue(row.discrepancy_rule_id))
      .filter(Boolean),
  ).size;

  return {
    totalIssues: issuesData.length,
    distinctSubjects,
    distinctSites,
    distinctRules,
    avgDaysOpen: getAverageDaysOpen(issuesData).toFixed(1),
    byStudyStatus: toCountRows(countBy(issuesData, "study_status")),
    bySource: toCountRows(
      countBy(issuesData, "discrepancy_data_source"),
      "Issues",
      6,
    ),
    byAssignee: toCountRows(
      countBy(issuesData, "discrepancy_assignee"),
      "Issues",
      6,
    ),
    byCategory: toCountRows(
      countBy(issuesData, "discrepancy_category"),
      "Issues",
      8,
    ),
    ageBuckets: toCountRows(createAgeBuckets(issuesData), "Issues", undefined, {
      sort: false,
    }),
    rulesBySource: toCountRows(
      countBy(rulesData, "discrepancy_rule_data_source"),
      "Rules",
    ),
  };
};
